import type { AccionTarea, ConfiguracionIA, Tarea } from '../types';

// Type for Parse SDK from window
interface ParseSDK {
  applicationId: string;
  serverURL: string;
  Object: {
    extend: (className: string) => any;
  };
  Query: new (objectClass: any) => any;
  User: {
    current: () => any;
  };
}

// Use Parse from parent window instead of bundling our own
function getParse(): ParseSDK | null {
  const parentParse = (window as any).Parse;
  if (parentParse && parentParse.applicationId) {
    return parentParse as ParseSDK;
  }
  return null;
}

// Initialize Parse - validates that parent Parse is available
export function initParse(_config?: { appId: string; serverURL: string; sessionToken?: string }) {
  const Parse = getParse();
  if (!Parse) {
    console.error('[FlowEditor] Parse not found');
    return false;
  }
  return true;
}

// Fetch all actions for a task
export async function fetchAccionesTarea(tareaId: string): Promise<AccionTarea[]> {
  const Parse = getParse();
  if (!Parse) {
    throw new Error('Parse SDK not available');
  }

  const AccionesTareaClass = Parse.Object.extend('AccionesTarea');
  const TareasClass = Parse.Object.extend('Tareas');

  const query = new Parse.Query(AccionesTareaClass);
  query.equalTo('Tarea', TareasClass.createWithoutData(tareaId));
  query.equalTo('Eliminada', false);
  query.notEqualTo('Eliminada', true);
  query.ascending('Orden');
  query.limit(1000);

  // Include related objects
  query.include('Padre');
  query.include('Posta');
  query.include('Grupo');

  const results = await query.find();

  return results.map((obj: any) => ({
    objectId: obj.id || '',
    CodigoUnico: obj.get('CodigoUnico') || '',
    Texto: obj.get('Texto') || obj.get('Nombre') || '',
    Tipo: obj.get('Tipo') || '',
    Orden: obj.get('Orden') || 0,
    OrdenHijo: obj.get('OrdenHijo'),
    Padre: obj.get('Padre') ? {
      __type: 'Pointer' as const,
      className: 'AccionesTarea',
      objectId: obj.get('Padre').id || ''
    } : undefined,
    SaltarA: obj.get('SaltarA'),
    Finaliza: obj.get('Finaliza'),
    Foto: obj.get('Foto'),
    Activa: obj.get('Activa'),
    EsDeGrupoDeAcciones: obj.get('EsDeGrupoDeAcciones'),
    AccionOpcion: obj.get('AccionOpcion'),
    AlternativaOpcion: obj.get('AlternativaOpcion'),
    Posta: obj.get('Posta') ? {
      __type: 'Pointer' as const,
      className: 'Grupos',
      objectId: obj.get('Posta').id || ''
    } : undefined,
    Grupo: obj.get('Grupo') ? {
      __type: 'Pointer' as const,
      className: 'Grupos',
      objectId: obj.get('Grupo').id || ''
    } : undefined,
    MostrarSiVieneDe: obj.get('MostrarSiVieneDe'),
    SoloMostrarSiVieneDe: obj.get('SoloMostrarSiVieneDe'),
  }));
}

// Fetch task info
export async function fetchTarea(tareaId: string): Promise<Tarea | null> {
  const Parse = getParse();
  if (!Parse) {
    throw new Error('Parse SDK not available');
  }

  const TareasClass = Parse.Object.extend('Tareas');
  const query = new Parse.Query(TareasClass);

  try {
    const result = await query.get(tareaId);
    return {
      objectId: result.id || '',
      Nombre: result.get('Nombre') || result.get('nombre') || 'Sin nombre',
      Descripcion: result.get('Descripcion'),
    };
  } catch (error) {
    console.error('[FlowEditor] Error fetching tarea:', error);
    return null;
  }
}

// Fetch IA configuration for a task
export async function fetchConfiguracionIA(tareaId: string): Promise<ConfiguracionIA | null> {
  const Parse = getParse();
  if (!Parse) {
    throw new Error('Parse SDK not available');
  }

  const ConfiguracionIAClass = Parse.Object.extend('ConfiguracionIA');
  const TareasClass = Parse.Object.extend('Tareas');

  const query = new Parse.Query(ConfiguracionIAClass);
  query.equalTo('tarea', TareasClass.createWithoutData(tareaId));
  query.doesNotExist('accionId'); // Global config (not per-action)

  try {
    const result = await query.first();
    if (!result) return null;

    return {
      objectId: result.id || '',
      activo: result.get('activo') || false,
      accionesConfig: result.get('accionesConfig') || {},
      analisisGlobal: result.get('analisisGlobal'),
    };
  } catch (error) {
    console.error('[FlowEditor] Error fetching IA config:', error);
    return null;
  }
}

// Get Grupo name by ID
export async function fetchGrupoNombre(grupoId: string): Promise<string> {
  const Parse = getParse();
  if (!Parse) {
    return 'Grupo';
  }

  const GruposClass = Parse.Object.extend('Grupos');
  const query = new Parse.Query(GruposClass);

  try {
    const result = await query.get(grupoId);
    return result.get('Nombre') || result.get('nombre') || 'Grupo';
  } catch (error) {
    return 'Grupo';
  }
}

// Helper to check if action has IA configured
export function getAccionIAConfig(
  accionId: string,
  codigoUnico: string,
  configIA: ConfiguracionIA | null
): { tieneIA: boolean; config?: ConfiguracionIA['accionesConfig'][string] } {
  if (!configIA || !configIA.activo) {
    return { tieneIA: false };
  }

  const accionesConfig = configIA.accionesConfig || {};

  // Try to find by objectId first
  if (accionesConfig[accionId]?.activo) {
    return {
      tieneIA: true,
      config: accionesConfig[accionId],
    };
  }

  // Try to find by codigoUnico
  for (const [id, config] of Object.entries(accionesConfig)) {
    if (config.activo && id === codigoUnico) {
      return {
        tieneIA: true,
        config,
      };
    }
  }

  return { tieneIA: false };
}
